import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { ProxmoxRestoreModelForStepsHelper } from 'wizards/helpers/proxmox-restore-model-helpers/proxmox-restore-model-for-steps-helper';
import { ProxmoxRestorePlanFromStepsHelpers } from 'wizards/helpers/proxmox-restore-model-helpers/proxmox-restore-plan-from-steps-helpers';
import { RestoreType } from 'wizards/models/restore-point-models';
import { RestoreProxmoxFormModel } from 'wizards/models/restoreProxmox/restore-proxmox-form-model';
import { RestoreAsDiskEnum } from 'wizards/models/type-data-models';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardBaseClass } from 'wizards/remote-management-wizards/wizard-base.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { WizardsServicesHubService } from 'wizards/services/wizards-services-hub.service';
import { BackupToRestoreStepComponent } from 'wizards/steps/backup-to-restore-step/backup-to-restore-step.component';
import { EncryptionOptionsStepComponent } from 'wizards/steps/encription-options-step/encryption-options-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { DestinationVMStepComponent } from 'wizards/steps/only-restore-steps/destination-vm-step/destination-vm-step.component';
import { TypeDataStepComponent } from 'wizards/steps/only-restore-steps/type-data-step/type-data-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { RestorePointStepComponent } from 'wizards/steps/restore-point-step/restore-point-step.component';
import { RestoreSourceStepComponent } from 'wizards/steps/restore-source-step/restore-source-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SelectComputerStepComponent } from 'wizards/steps/select-computer-step/select-computer-step.component';
import { SelectHostStepComponent } from 'wizards/steps/select-host-step/select-host-step.component';
import { StepLoadInfo } from 'wizards/steps/StepBase.class';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';

type RestoreProxmoxWizardHelpDocumentationLinks =
  | 'welcome'
  | 'storage'
  | 'selectComputer'
  | 'selectBackup'
  | 'selectPoint'
  | 'source'
  | 'typeOfData'
  | 'host'
  | 'destination'
  | 'encryption'
  | 'schedule'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-proxmox-restore-wizard',
  templateUrl: './proxmox-restore-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => ProxmoxRestoreWizardComponent)
    }
  ],
  imports: [
    AsyncPipe,
    BackupToRestoreStepComponent,
    CommonModule,
    DestinationVMStepComponent,
    EncryptionOptionsStepComponent,
    FormsModule,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RestorePointStepComponent,
    RestoreSourceStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SelectComputerStepComponent,
    SelectHostStepComponent,
    TypeDataStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe
  ]
})
export class ProxmoxRestoreWizardComponent extends WizardBaseClass {
  i18nPipe: I18NextPipe;

  public readonly restoreType = RestoreType;
  public readonly restoreAsDiskEnum = RestoreAsDiskEnum;
  public readonly restorePointTitle = this.i18nPipe.transform('wizards:restore_point_title');
  public readonly hostTitle = this.i18nPipe.transform('wizards:select_host_step_title');

  public helpLink: WizardHelpLink<RestoreProxmoxWizardHelpDocumentationLinks>;

  constructor() {
    const i18nPipe = inject(I18NextPipe);
    const services = inject(WizardsServicesHubService);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.FormModel = new RestoreProxmoxFormModel();

    this.initSubscribes();
  }

  protected updateStepData(plan, isDefault?: boolean): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) return this.setDefaultValue();

    this.FormModel = ProxmoxRestoreModelForStepsHelper.restoreVMModelFromPlan(plan, this.defaultSettings());

    this.stepsManager.showEncryptionOptionsStepIfNeed();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected loadInfoHandlerFinally(event?: StepLoadInfo): void {
    this.cdr.detectChanges();
  }

  restoreAsChangeHandler(type: RestoreAsDiskEnum): void {
    // If need see in VMWare restore
    this.cdr.detectChanges();
  }

  protected getPlanForSaving(): any {
    return ProxmoxRestorePlanFromStepsHelpers.getUpdatedPlan(
      this.loadedPlan.Plan || this.loadedPlan.plan,
      this.FormModel,
      this.defaultSettings()
    );
  }

  getRAWHelpLinks(): Record<RestoreProxmoxWizardHelpDocumentationLinks, string> {
    return {
      welcome: 'restore/Proxmox/restore-console#planname',
      storage: 'restore/Proxmox/restore-console#storage',
      selectComputer: 'restore/Proxmox/restore-console#comp',
      selectBackup: 'restore/Proxmox/restore-console#backup',
      selectPoint: 'restore/Proxmox/restore-console#rp',
      source: 'restore/Proxmox/restore-console#rs',
      typeOfData: 'restore/Proxmox/restore-console#restoreas',
      host: 'restore/Proxmox/restore-console#host',
      destination: 'restore/Proxmox/restore-console#destination',
      encryption: 'restore/Proxmox/restore-console#encryption',
      schedule: 'restore/Proxmox/restore-console#schedule',
      prePost: 'restore/Proxmox/restore-console#prepost',
      notification: 'restore/Proxmox/restore-console#notification'
    };
  }
}
